import React, { useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  styled,
  tableCellClasses,
  Stack,
  TablePagination,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: `#2C5F8E`,
    color: `#FFFFFF`,
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: "nowrap",
    fontFamily: "Geist, sans-serif",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: "Geist, sans-serif",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: `#EBF2FF`,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: "#D6E6F2",
    boxShadow: "0px 0px 5px 0px grey",
  },
}));

const statData = [
  { title: "Total Jobs Posted", value: 320 },
  { title: "Open Positions", value: 180 },
  { title: "Closed Positions", value: 120 },
  { title: "In Progress", value: 20 },
];

const jobStats = [
  { title: "Registered Nurse", applications: 220, shortlisted: 60, hired: 18 },
  { title: "ICU Nurse", applications: 140, shortlisted: 35, hired: 10 },
  { title: "ER Nurse", applications: 130, shortlisted: 30, hired: 9 },
  { title: "Pediatric Nurse", applications: 110, shortlisted: 28, hired: 7 },
  { title: "Home Care Nurse", applications: 160, shortlisted: 40, hired: 15 },
  { title: "General Physician", applications: 150, shortlisted: 40, hired: 12 },
  { title: "Radiologist", applications: 90, shortlisted: 25, hired: 7 },
];

const jobsOverTime = [
  { month: "Jan", jobs: 20 },
  { month: "Feb", jobs: 30 },
  { month: "Mar", jobs: 40 },
  { month: "Apr", jobs: 35 },
  { month: "May", jobs: 45 },
  { month: "Jun", jobs: 50 },
  { month: "Jul", jobs: 55 },
  { month: "Aug", jobs: 52 },
  { month: "Sep", jobs: 48 },
  { month: "Oct", jobs: 42 },
  { month: "Nov", jobs: 38 },
  { month: "Dec", jobs: 32 },
];

const StatCard = ({ title, value }) => (
  <Card
    sx={{
      minHeight: 100,
      textAlign: "center",
      boxShadow: "none",
      border: "1px solid lightgrey",
      borderRadius: "20px",
    }}
  >
    <CardContent>
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
      >
        {title}
      </Typography>
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
      >
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const JobPerformanceTable = ({ data, page, rowsPerPage }) => {
  const paginatedData = data.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <TableContainer
      sx={{
        border: "1px solid transparent",
        borderRadius: "10px",
        maxWidth: "90vw",
        overflowX: "auto",
        margin: "auto",
      }}
    >
      <Table>
        <TableHead>
          <TableRow>
            <StyledTableCell sx={{ width: "50px" }}>#</StyledTableCell>
            <StyledTableCell>Job Title</StyledTableCell>
            <StyledTableCell>Applications</StyledTableCell>
            <StyledTableCell>Shortlisted</StyledTableCell>
            <StyledTableCell>Hired</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedData.map((job, idx) => (
            <StyledTableRow key={idx}>
              <StyledTableCell>{page * rowsPerPage + idx + 1}</StyledTableCell>
              <StyledTableCell>{job.title}</StyledTableCell>
              <StyledTableCell>{job.applications}</StyledTableCell>
              <StyledTableCell>{job.shortlisted}</StyledTableCell>
              <StyledTableCell>{job.hired}</StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const JobsChart = ({ data }) => (
  <Paper sx={{ mt: 4, p: 2 }}>
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      mb={2}
    >
      <Typography variant="h6">Jobs Posted Over Time</Typography>
    </Stack>
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="month" />
        <YAxis />
        <Tooltip />
        <Bar dataKey="jobs" fill="#006CB8" />
      </BarChart>
    </ResponsiveContainer>
  </Paper>
);

const JobReportView = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
        mb={1}
      >
        Job Reports
      </Typography>

      <Grid container spacing={2}>
        {statData.map((item, idx) => (
          <Grid item xs={12} sm={6} md={3} key={idx}>
            <StatCard title={item.title} value={item.value} />
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
          mb={1}
        >
          Job Performance
        </Typography>

        <JobPerformanceTable
          data={jobStats}
          page={page}
          rowsPerPage={rowsPerPage}
        />

        <TablePagination
          component="div"
          count={jobStats.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 75, 100]}
          sx={{
            fontSize: { xs: "12px", sm: "14px" },
            "& .MuiTablePagination-selectLabel": {
              fontSize: { xs: "12px", sm: "14px" },
              fontFamily: "Geist, sans-serif",
            },
          }}
        />
      </Box>

      <JobsChart data={jobsOverTime} />
    </Box>
  );
};

export default JobReportView;
